import { useState, useMemo } from "react";
import { C, STEP_BASED, EXERCISE_LIBRARY } from "../constants";
import { toDisplayWeight, toDisplayDist, weightLabel } from "../utils/units";
import { getSortedHistory, getProgressionSuggestion } from "../utils/progression";
import { Badge } from "./ui";

export const ProgressionTab = ({ workouts, settings, programs, activeProgramId, customExercises = [], userExerciseOverrides = {} }) => {
  const [query,           setQuery]           = useState("");
  const [selected,        setSelected]        = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [expandedKey,     setExpandedKey]     = useState(null);

  const exerciseList = useMemo(() => {
    const strengthNames = [...new Set(workouts.filter(w => w.type === "strength").map(w => w.name))].sort();
    const cardioTypes   = [...new Set(workouts.filter(w => w.type === "cardio").map(w => w.cardioType))].sort();
    return [
      ...strengthNames.map(name => ({ name, kind: "strength" })),
      ...cardioTypes.map(name   => ({ name, kind: "cardio" })),
    ];
  }, [workouts]);

  const recent = useMemo(() => {
    const cutoff    = new Date(); cutoff.setDate(cutoff.getDate() - 30);
    const cutoffStr = cutoff.toISOString().split("T")[0];
    const countFrom = (minDate) => {
      const counts = {};
      workouts.filter(w => w.date >= minDate).forEach(w => {
        const name = w.type === "strength" ? w.name : w.cardioType;
        const key  = `${w.type}:${name}`;
        counts[key] = (counts[key] || 0) + 1;
      });
      return counts;
    };
    let counts   = countFrom(cutoffStr);
    const isRecent = Object.keys(counts).length > 0;
    if (!isRecent) counts = countFrom("0000-00-00");
    return {
      items: Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 10).map(([key, count]) => {
        const sep  = key.indexOf(":");
        const kind = key.slice(0, sep);
        const name = key.slice(sep + 1);
        return { name, kind, count };
      }),
      isRecent,
    };
  }, [workouts]);

  const filteredSuggestions = query.length > 0
    ? exerciseList.filter(e => e.name.toLowerCase().includes(query.toLowerCase()))
    : exerciseList;

  const strengthHistory = useMemo(() => {
    if (!selected || selected.kind !== "strength") return [];
    const sessions = workouts
      .filter(w => w.type === "strength" && w.name.toLowerCase() === selected.name.toLowerCase())
      .reduce((acc, w) => {
        const key = w.sessionId || w.date;
        if (!acc[key]) acc[key] = { date: w.date, sets: [], sessionKey: key };
        acc[key].sets.push({ weight: w.weight, reps: w.reps });
        return acc;
      }, {});
    const unsorted = Object.values(sessions).map(s => {
      const best = s.sets.reduce((b, set) => {
        const w1rm = set.weight > 0 ? set.weight * (1 + set.reps / 30) : set.reps;
        return w1rm > b.w1rm ? { weight: set.weight, reps: set.reps, w1rm } : b;
      }, { weight: 0, reps: 0, w1rm: 0 });
      return { date: s.date, sets: s.sets, best, sessionKey: s.sessionKey };
    });
    return getSortedHistory(unsorted);
  }, [workouts, selected]);

  const cardioHistory = useMemo(() => {
    if (!selected || selected.kind !== "cardio") return [];
    const isStep = STEP_BASED.includes(selected.name);
    return workouts
      .filter(w => w.type === "cardio" && w.cardioType === selected.name)
      .map(w => {
        const entry = { date: w.date, duration: w.duration, distance: w.distance, calories: w.calories };
        if (isStep) {
          entry.stepsPerMin = w.duration > 0 ? w.distance / w.duration : 0;
        } else {
          const dispDist = toDisplayDist(w.distance, settings.distUnit);
          entry.pace  = dispDist > 0 ? w.duration / dispDist : 0;
          entry.speed = w.duration > 0 ? dispDist / (w.duration / 60) : 0;
        }
        return entry;
      })
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [workouts, selected, settings.distUnit]);

  const exerciseConfig = useMemo(() => {
    const fallback = { repRange: { min: 8, max: 12 } };
    if (!selected || selected.kind !== "strength" || !programs?.length) return fallback;
    const active = programs.find(p => p.id === activeProgramId) || programs[0];
    if (!active) return fallback;
    for (const plan of active.plans) {
      const ex = plan.exercises.find(e => e.name.toLowerCase() === selected.name.toLowerCase());
      if (ex) return { repRange: ex.repRange || fallback.repRange };
    }
    return fallback;
  }, [selected, programs, activeProgramId]);

  const wUnit = settings.weightUnit;
  const wl    = weightLabel(wUnit);

  const fmtDate = (d, showYear = false) => new Date(d + "T12:00:00").toLocaleDateString("en-US", {
    month: "short", day: "numeric", ...(showYear ? { year: "2-digit" } : {}),
  });
  const fmtPace = mins => {
    if (!mins || !isFinite(mins)) return "—";
    const m = Math.floor(mins);
    const s = Math.round((mins - m) * 60);
    return `${m}:${String(s).padStart(2, "0")}`;
  };

  // Chart builder
  const W = 640, H = 180;
  const PAD = { top: 16, right: 16, bottom: 32, left: 48 };
  const chartW = W - PAD.left - PAD.right;
  const chartH = H - PAD.top  - PAD.bottom;

  const buildChart = (points, opts = {}) => {
    if (!points || points.length < 2) return null;
    const vals     = points.map(p => p.value);
    const rangeLow = Math.max(0, Math.min(...vals) * 0.9);
    const rangeHigh = Math.max(...vals) * 1.08;
    const minV = rangeLow, maxV = rangeHigh, range = maxV - minV || 1;
    const plotted = points.map((p, i) => ({
      ...p,
      x: (i / (points.length - 1)) * chartW + PAD.left,
      y: chartH - ((p.value - minV) / range) * chartH + PAD.top,
    }));
    const pathD = plotted.map((p, i) => {
      if (i === 0) return `M ${p.x} ${p.y}`;
      const prev = plotted[i - 1], cx = (prev.x + p.x) / 2;
      return `C ${cx} ${prev.y} ${cx} ${p.y} ${p.x} ${p.y}`;
    }).join(" ");
    const areaD = pathD + ` L ${plotted[plotted.length-1].x} ${PAD.top + chartH} L ${plotted[0].x} ${PAD.top + chartH} Z`;
    const yTicks = Array.from({ length: 5 }, (_, i) => {
      const v = minV + (range * i) / 4;
      return { y: chartH - ((v - minV) / range) * chartH + PAD.top, label: opts.formatY ? opts.formatY(v) : Math.round(v) };
    });
    return { plotted, pathD, areaD, yTicks };
  };

  const strengthChart = useMemo(() => {
    if (strengthHistory.length === 0) return null;
    return buildChart(strengthHistory.map(h => ({ value: toDisplayWeight(h.best.weight > 0 ? h.best.weight : 0, wUnit), date: h.date })));
  }, [strengthHistory, wUnit]);

  const cardioSummary = useMemo(() => {
    if (cardioHistory.length === 0) return null;
    const isStep    = STEP_BASED.includes(selected?.name);
    const totalDur  = cardioHistory.reduce((s, h) => s + h.duration, 0);
    const totalCals = cardioHistory.reduce((s, h) => s + h.calories, 0);
    if (isStep) {
      const totalSteps = cardioHistory.reduce((s, h) => s + h.distance, 0);
      const avgSpm     = totalDur > 0 ? totalSteps / totalDur : 0;
      const bestSpm    = Math.max(...cardioHistory.map(h => h.stepsPerMin));
      return { isStep: true, sessions: cardioHistory.length, totalDur, totalCals, totalSteps, avgSpm, bestSpm };
    }
    const totalDist        = cardioHistory.reduce((s, h) => s + h.distance, 0);
    const displayTotalDist = toDisplayDist(totalDist, settings.distUnit);
    const paces            = cardioHistory.map(h => h.pace).filter(p => p > 0);
    const avgPace          = paces.length > 0 ? totalDur / displayTotalDist : 0;
    const bestPace         = paces.length > 0 ? Math.min(...paces) : 0;
    return { isStep: false, sessions: cardioHistory.length, totalDur, totalCals, totalDist: displayTotalDist, avgPace, bestPace };
  }, [cardioHistory, selected, settings.distUnit]);

  const paceChart = useMemo(() => {
    if (!cardioSummary || cardioSummary.isStep || cardioHistory.length < 2) return null;
    return buildChart(cardioHistory.map(h => ({ value: h.pace, date: h.date })), { formatY: v => fmtPace(v), lowerIsBetter: true });
  }, [cardioHistory, cardioSummary]);

  const spmChart = useMemo(() => {
    if (!cardioSummary || !cardioSummary.isStep || cardioHistory.length < 2) return null;
    return buildChart(cardioHistory.map(h => ({ value: h.stepsPerMin, date: h.date })));
  }, [cardioHistory, cardioSummary]);

  const renderChart = (chart, label) => (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: "18px 18px 10px", marginBottom: 16 }}>
      <div style={{ color: C.muted, fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 14 }}>{label}</div>
      {chart ? (
        <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: "auto", display: "block" }}>
          {chart.yTicks.map((t, i) => (
            <g key={i}>
              <line x1={PAD.left} y1={t.y} x2={W - PAD.right} y2={t.y} stroke={C.border} strokeWidth="1" strokeDasharray="3,4" />
              <text x={PAD.left - 6} y={t.y + 4} textAnchor="end" fontSize="10" fill={C.muted}>{t.label}</text>
            </g>
          ))}
          <path d={chart.areaD} fill={C.blue} opacity="0.12" />
          <path d={chart.pathD} fill="none" stroke={C.blue} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          {chart.plotted.map((p, i) => (
            <g key={i}>
              <circle cx={p.x} cy={p.y} r="4.5" fill={C.blue} stroke={C.card} strokeWidth="2" />
              {(chart.plotted.length <= 8 || i % Math.ceil(chart.plotted.length / 8) === 0) && (
                <text x={p.x} y={H - 4} textAnchor="middle" fontSize="9" fill={C.muted}>
                  {new Date(p.date + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                </text>
              )}
            </g>
          ))}
        </svg>
      ) : (
        <div style={{ color: C.muted, fontSize: 13, textAlign: "center", padding: "20px 0" }}>Log more sessions to see your trend.</div>
      )}
    </div>
  );

  return (
    <div>
      {/* Search */}
      <div style={{ marginBottom: 24, position: "relative" }}>
        <div style={{ color: C.muted, fontSize: 11, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 8 }}>Search Exercise</div>
        <div style={{ position: "relative" }}>
          <input
            value={query}
            onChange={e => { setQuery(e.target.value); setShowSuggestions(true); if (!e.target.value) setSelected(null); }}
            onFocus={() => setShowSuggestions(true)}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
            placeholder="e.g. Bench Press, Running..."
            style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, color: C.text, padding: "12px 16px", fontSize: 15, width: "100%", outline: "none" }}
          />
          {selected && <div style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", color: C.green, fontSize: 13, fontWeight: 600 }}>✓</div>}
        </div>
        {showSuggestions && filteredSuggestions.length > 0 && (
          <div style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 20, background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, marginTop: 4, maxHeight: 260, overflowY: "auto", boxShadow: "0 8px 24px rgba(0,0,0,0.35)" }}>
            {filteredSuggestions.map(item => (
              <div key={`${item.kind}:${item.name}`}
                onMouseDown={() => { setSelected(item); setQuery(item.name); setShowSuggestions(false); setExpandedKey(null); }}
                style={{ padding: "11px 16px", cursor: "pointer", fontSize: 14, display: "flex", justifyContent: "space-between", alignItems: "center", color: C.text, background: selected?.name === item.name && selected?.kind === item.kind ? C.surface : "transparent", borderBottom: `1px solid ${C.border}` }}>
                <span>{item.name}</span>
                <Badge color={item.kind === "strength" ? "accent" : "blue"}>{item.kind}</Badge>
              </div>
            ))}
          </div>
        )}
        {query && filteredSuggestions.length === 0 && <div style={{ color: C.muted, fontSize: 13, marginTop: 8 }}>No matches for "{query}"</div>}
      </div>

      {/* Empty state */}
      {!selected && (
        <div style={{ textAlign: "center", padding: "52px 0" }}>
          <div style={{ fontSize: 42, marginBottom: 14 }}>📈</div>
          <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, fontWeight: 800, color: C.text, marginBottom: 8 }}>Progression Tracker</div>
          <div style={{ color: C.muted, fontSize: 14, lineHeight: 1.6, maxWidth: 320, margin: "0 auto" }}>Search for an exercise to see your progress over time — weights, paces, and personal records.</div>
          {recent.items.length > 0 && (
            <div style={{ marginTop: 28 }}>
              <div style={{ color: C.muted, fontSize: 11, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 12 }}>
                {recent.isRecent ? "Most Logged — Last 30 Days" : "Most Logged — All Time"}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6, maxWidth: 380, margin: "0 auto" }}>
                {recent.items.map(item => (
                  <button key={`${item.kind}:${item.name}`}
                    onClick={() => { setSelected({ name: item.name, kind: item.kind }); setQuery(item.name); setExpandedKey(null); }}
                    style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 16px", borderRadius: 9, border: `1px solid ${C.border}`, background: C.card, cursor: "pointer", textAlign: "left" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ color: C.text, fontSize: 14, fontWeight: 500 }}>{item.name}</span>
                      <Badge color={item.kind === "strength" ? "accent" : "blue"}>{item.kind}</Badge>
                    </div>
                    <span style={{ color: C.muted, fontSize: 12 }}>{item.count} {item.kind === "strength" ? (item.count === 1 ? "set" : "sets") : (item.count === 1 ? "session" : "sessions")}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Strength view */}
      {selected?.kind === "strength" && strengthHistory.length === 0 && (
        <div style={{ textAlign: "center", padding: "48px 0", color: C.muted }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>🔍</div>
          <div style={{ fontSize: 14 }}>No logged sets found for <strong style={{ color: C.text }}>{selected.name}</strong></div>
        </div>
      )}
      {selected?.kind === "strength" && strengthHistory.length > 0 && (() => {
        const first  = strengthHistory[0], last = strengthHistory[strengthHistory.length - 1];
        const firstW = toDisplayWeight(first.best.weight, wUnit);
        const lastW  = toDisplayWeight(last.best.weight, wUnit);
        const diff   = +(lastW - firstW).toFixed(1);
        const pct    = firstW > 0 ? ((diff / firstW) * 100).toFixed(1) : 0;
        const pr     = Math.max(...strengthHistory.map(h => toDisplayWeight(h.best.weight, wUnit)));
        let gains = 0, drops = 0;
        for (let i = 1; i < strengthHistory.length; i++) {
          if (strengthHistory[i].best.weight > strengthHistory[i-1].best.weight) gains++;
          else if (strengthHistory[i].best.weight < strengthHistory[i-1].best.weight) drops++;
        }

        const { repRange } = exerciseConfig;
        const isBodyweight = last.best.weight === 0;
        const lowerName    = selected.name.toLowerCase();
        const baseEntry    = customExercises.find(e => e.name.toLowerCase() === lowerName)
                          || EXERCISE_LIBRARY.find(e => e.name.toLowerCase() === lowerName);
        const baseId       = baseEntry?.id || null;
        const ov           = (baseId && userExerciseOverrides[baseId]) || userExerciseOverrides[lowerName] || {};
        const libEntry     = baseEntry ? { ...baseEntry, ...ov } : null;
        const overrideInc  = libEntry?.useCustomIncrement && libEntry.increment > 0
          ? libEntry.increment
          : undefined;
        const suggestion   = getProgressionSuggestion({
          workouts,
          exerciseId: baseId,
          exerciseName: selected.name,
          repRange,
          unit: wUnit,
          increment: overrideInc,
          progressionDirection: libEntry?.progressionDirection || "up",
        });

        return (
          <>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 20 }}>
              <div>
                <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, fontWeight: 800, color: C.text, lineHeight: 1 }}>{selected.name}</div>
                <div style={{ color: C.muted, fontSize: 12, marginTop: 5 }}>{strengthHistory.length} session{strengthHistory.length !== 1 ? "s" : ""} · {fmtDate(first.date, true)} — {fmtDate(last.date, true)}</div>
              </div>
              {strengthHistory.length >= 2 && (
                <div style={{ background: diff >= 0 ? C.green + "22" : C.red + "22", border: `1px solid ${diff >= 0 ? C.green + "55" : C.red + "55"}`, borderRadius: 10, padding: "8px 16px", textAlign: "center" }}>
                  <div style={{ fontSize: 20, fontWeight: 800, color: diff >= 0 ? C.green : C.red }}>{diff >= 0 ? "+" : ""}{diff} {wl}</div>
                  <div style={{ fontSize: 11, color: C.muted }}>{diff >= 0 ? "+" : ""}{pct}% overall</div>
                </div>
              )}
            </div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 20 }}>
              {[
                { label: "Personal Record", value: `${pr} ${wl}`, hi: true },
                { label: "Current",  value: `${lastW} ${wl}` },
                { label: "Starting", value: `${firstW} ${wl}` },
                { label: "Sessions", value: strengthHistory.length },
                { label: "Increases", value: gains },
                { label: "Decreases", value: drops },
              ].map(s => (
                <div key={s.label} style={{ background: C.card, border: `1px solid ${s.hi ? C.blue + "55" : C.border}`, borderRadius: 10, padding: "12px 16px", flex: "1 1 80px", minWidth: 80 }}>
                  <div style={{ color: C.muted, fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 5 }}>{s.label}</div>
                  <div style={{ color: s.hi ? C.blue : C.text, fontSize: 18, fontWeight: 700, lineHeight: 1 }}>{s.value}</div>
                </div>
              ))}
            </div>
            {suggestion !== null && (() => {
              const titleByType = {
                progress: "Increase Weight",
                decrease: "Decrease Weight",
                maintain: "Rep Progression",
                deload:   "Deload",
              };
              const isMaintain  = suggestion.type === "maintain";
              const accentColor = isMaintain ? C.accent : C.blue;
              return (
                <div style={{ background: C.card, border: `1px solid ${accentColor}55`, borderRadius: 12, padding: "16px 20px", marginBottom: 16 }}>
                  <div style={{ color: C.muted, fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>
                    {titleByType[suggestion.type] || "Suggestion"}
                  </div>
                  {suggestion.mode === "exact" ? (
                    <>
                      <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginBottom: suggestion.repSuggestion ? 8 : 4 }}>
                        {isBodyweight ? (
                          <span style={{ fontSize: 22, fontWeight: 800, color: C.accent, lineHeight: 1 }}>Bodyweight</span>
                        ) : (
                          <>
                            <span style={{ fontSize: 28, fontWeight: 800, color: accentColor, lineHeight: 1 }}>{suggestion.weight}</span>
                            <span style={{ fontSize: 15, color: C.muted }}>{wl}</span>
                          </>
                        )}
                      </div>
                      {suggestion.repSuggestion && (
                        <div style={{ display: "inline-flex", alignItems: "center", marginBottom: 8, background: C.accent + "18", border: `1px solid ${C.accent}55`, borderRadius: 6, padding: "4px 12px" }}>
                          <span style={{ fontSize: 13, fontWeight: 700, color: C.accent }}>{suggestion.repSuggestion}</span>
                        </div>
                      )}
                    </>
                  ) : (
                    <div style={{ display: "inline-flex", alignItems: "center", marginBottom: 8, background: accentColor + "18", border: `1px solid ${accentColor}55`, borderRadius: 6, padding: "6px 14px" }}>
                      <span style={{ fontSize: 14, fontWeight: 700, color: accentColor }}>{suggestion.message}</span>
                    </div>
                  )}
                  <div style={{ color: C.muted, fontSize: 12, marginBottom: 6 }}>Based on your rep range performance</div>
                  <div style={{ display: "inline-block", background: C.blue + "18", border: `1px solid ${C.blue}44`, borderRadius: 6, padding: "2px 10px", fontSize: 11, fontWeight: 600, color: C.blue }}>
                    Target: {repRange.min}–{repRange.max} reps{suggestion.mode === "exact" && !suggestion.repSuggestion ? ` · +${suggestion.incrementUsed} ${wl} step` : ""}
                  </div>
                </div>
              );
            })()}
            {renderChart(strengthChart, `Weight Over Time (${wl})`)}
            <div style={{ color: C.muted, fontSize: 11, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 12 }}>Session History</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {[...strengthHistory].reverse().map((h, i, arr) => {
                const prev       = arr[i + 1];
                const dispW      = toDisplayWeight(h.best.weight, wUnit);
                const prevW      = prev ? toDisplayWeight(prev.best.weight, wUnit) : null;
                const d          = prevW !== null ? +(dispW - prevW).toFixed(1) : null;
                const allWeights = strengthHistory.map(x => toDisplayWeight(x.best.weight, wUnit));
                const isPR       = h.best.weight > 0 && dispW >= Math.max(...allWeights);
                const isExpanded = expandedKey === h.sessionKey;
                return (
                  <div key={h.sessionKey} style={{ background: C.card, border: `1px solid ${isPR ? C.blue + "55" : C.border}`, borderRadius: 10, overflow: "hidden" }}>
                    <div
                      onClick={() => setExpandedKey(isExpanded ? null : h.sessionKey)}
                      style={{ padding: "13px 16px", display: "flex", alignItems: "center", gap: 12, cursor: "pointer" }}
                    >
                      <div style={{ minWidth: 78, color: C.textDim, fontSize: 12 }}>{fmtDate(h.date, true)}</div>
                      <div style={{ flex: 1 }}>
                        <span style={{ fontWeight: 700, fontSize: 16, color: C.text }}>{h.best.weight > 0 ? `${dispW} ${wl}` : "Bodyweight"}</span>
                        <span style={{ color: C.muted, fontSize: 12, marginLeft: 8 }}>{h.sets.length} set{h.sets.length !== 1 ? "s" : ""}</span>
                      </div>
                      {d !== null && (
                        <div style={{ padding: "3px 10px", borderRadius: 6, fontSize: 12, fontWeight: 700, background: d > 0 ? C.green + "22" : d < 0 ? C.red + "22" : C.surface, color: d > 0 ? C.green : d < 0 ? C.red : C.muted }}>
                          {d > 0 ? `+${d}` : d < 0 ? `${d}` : "—"}
                        </div>
                      )}
                      {isPR && <div style={{ padding: "3px 10px", borderRadius: 6, fontSize: 11, fontWeight: 700, background: C.blue + "22", color: C.blue }}>PR</div>}
                      <div style={{ color: C.muted, fontSize: 12, flexShrink: 0 }}>{isExpanded ? "▲" : "▼"}</div>
                    </div>
                    {isExpanded && (
                      <div style={{ borderTop: `1px solid ${C.border}`, padding: "10px 16px 14px", display: "flex", flexDirection: "column", gap: 6 }}>
                        {h.sets.map((s, si) => {
                          const sw = toDisplayWeight(s.weight, wUnit);
                          return (
                            <div key={si} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                              <div style={{ width: 48, fontSize: 11, color: C.muted, fontWeight: 600 }}>Set {si + 1}</div>
                              <div style={{ fontSize: 13, color: C.text, fontWeight: 600 }}>
                                {s.weight > 0 ? `${sw} ${wl}` : "Bodyweight"}
                              </div>
                              <div style={{ fontSize: 13, color: C.muted }}>×</div>
                              <div style={{ fontSize: 13, color: C.accent, fontWeight: 700 }}>{s.reps} reps</div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        );
      })()}

      {/* Cardio view */}
      {selected?.kind === "cardio" && cardioHistory.length === 0 && (
        <div style={{ textAlign: "center", padding: "48px 0", color: C.muted }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>🔍</div>
          <div style={{ fontSize: 14 }}>No logged sessions for <strong style={{ color: C.text }}>{selected.name}</strong></div>
        </div>
      )}
      {selected?.kind === "cardio" && cardioHistory.length > 0 && cardioSummary && (
        <>
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, fontWeight: 800, color: C.text }}>{selected.name}</div>
            <div style={{ color: C.muted, fontSize: 12, marginTop: 5 }}>{cardioSummary.sessions} session{cardioSummary.sessions !== 1 ? "s" : ""} · {fmtDate(cardioHistory[0].date, true)} — {fmtDate(cardioHistory[cardioHistory.length - 1].date, true)}</div>
          </div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 20 }}>
            {(cardioSummary.isStep ? [
              { label: "Best Pace",   value: `${Math.round(cardioSummary.bestSpm)} spm`, hi: true },
              { label: "Avg Pace",    value: `${Math.round(cardioSummary.avgSpm)} spm` },
              { label: "Total Steps", value: cardioSummary.totalSteps.toLocaleString() },
              { label: "Total Time",  value: `${Math.round(cardioSummary.totalDur)} min` },
              { label: "Calories",    value: cardioSummary.totalCals.toLocaleString() },
              { label: "Sessions",    value: cardioSummary.sessions },
            ] : [
              { label: "Best Pace",  value: `${fmtPace(cardioSummary.bestPace)} /${settings.distUnit}`, hi: true },
              { label: "Avg Pace",   value: `${fmtPace(cardioSummary.avgPace)} /${settings.distUnit}` },
              { label: "Total Dist", value: `${cardioSummary.totalDist.toFixed(1)} ${settings.distUnit}` },
              { label: "Total Time", value: `${Math.round(cardioSummary.totalDur)} min` },
              { label: "Calories",   value: cardioSummary.totalCals.toLocaleString() },
              { label: "Sessions",   value: cardioSummary.sessions },
            ]).map(s => (
              <div key={s.label} style={{ background: C.card, border: `1px solid ${s.hi ? C.blue + "55" : C.border}`, borderRadius: 10, padding: "12px 16px", flex: "1 1 80px", minWidth: 80 }}>
                <div style={{ color: C.muted, fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 5 }}>{s.label}</div>
                <div style={{ color: s.hi ? C.blue : C.text, fontSize: 17, fontWeight: 700, lineHeight: 1 }}>{s.value}</div>
              </div>
            ))}
          </div>
          {cardioSummary.isStep ? renderChart(spmChart, "Steps Per Minute Over Time") : renderChart(paceChart, `Pace Over Time (min/${settings.distUnit})`)}
          <div style={{ color: C.muted, fontSize: 11, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 12 }}>Session History</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {[...cardioHistory].reverse().map(h => {
              const isStep = cardioSummary.isStep;
              return (
                <div key={`${h.date}_${h.duration}_${h.distance}`} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: "13px 16px", display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ minWidth: 78, color: C.textDim, fontSize: 12 }}>{fmtDate(h.date, true)}</div>
                  <div style={{ flex: 1, display: "flex", flexWrap: "wrap", gap: 10 }}>
                    <div><div style={{ fontSize: 10, color: C.muted, marginBottom: 1 }}>Time</div><div style={{ fontWeight: 700, fontSize: 14, color: C.text }}>{h.duration} min</div></div>
                    <div>
                      <div style={{ fontSize: 10, color: C.muted, marginBottom: 1 }}>{isStep ? "Steps" : "Distance"}</div>
                      <div style={{ fontWeight: 700, fontSize: 14, color: C.text }}>
                        {isStep ? h.distance.toLocaleString() : `${toDisplayDist(h.distance, settings.distUnit).toFixed(2)} ${settings.distUnit}`}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: 10, color: C.muted, marginBottom: 1 }}>{isStep ? "SPM" : "Pace"}</div>
                      <div style={{ fontWeight: 700, fontSize: 14, color: C.blue }}>
                        {isStep ? Math.round(h.stepsPerMin) : `${fmtPace(h.pace)}/${settings.distUnit}`}
                      </div>
                    </div>
                    <div><div style={{ fontSize: 10, color: C.muted, marginBottom: 1 }}>Cal</div><div style={{ fontWeight: 700, fontSize: 14, color: C.text }}>{h.calories}</div></div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};
